/**
 * 防重放时间戳验证
 */

/**
 * 防重放验证选项
 */
export interface ReplayGuardOptions {
  /** 时间窗口 (秒)，默认 60 */
  window?: number;
  /** 允许的前向偏差 (秒)，默认 5 (处理客户端时钟快于服务端的情况) */
  forwardSkew?: number;
}

/**
 * 验证时间戳是否在有效窗口内 (防重放攻击)
 *
 * @param timestamp - 要验证的时间戳 (毫秒)
 * @param options - 验证选项
 * @returns 是否有效
 *
 * @example
 * ```ts
 * const ts = Date.now();
 * validateTimestamp(ts) // true
 * validateTimestamp(ts - 120000) // false (超过60秒窗口)
 * validateTimestamp(ts + 10000) // false (超过5秒前向偏差)
 * ```
 */
export function validateTimestamp(
  timestamp: number,
  options?: ReplayGuardOptions,
): boolean {
  const windowMs = (options?.window ?? 60) * 1000;
  const forwardSkewMs = (options?.forwardSkew ?? 5) * 1000;
  const now = Date.now();

  // 检查时间戳不在未来太远
  if (timestamp > now + forwardSkewMs) {
    return false;
  }

  // 检查时间戳不在过去太远
  if (now - timestamp > windowMs) {
    return false;
  }

  return true;
}

/**
 * 生成当前时间戳 (毫秒)
 *
 * @returns 当前时间戳
 */
export function generateTimestamp(): number {
  return Date.now();
}

/**
 * 创建防重放守卫
 *
 * 维护一个已用时间戳集合，防止同一时间戳被重复使用。
 *
 * @param options - 选项
 * @returns 守卫实例
 *
 * @example
 * ```ts
 * const guard = createReplayGuard({ window: 120 });
 *
 * guard.validate(timestamp) // true (首次)
 * guard.validate(timestamp) // false (重复)
 * ```
 */
export interface ReplayGuardConfig extends ReplayGuardOptions {
  /** 已使用时间戳集合的清理阈值，超过此数量触发自动清理。默认 1000 */
  cleanupThreshold?: number;
}

export function createReplayGuard(options?: ReplayGuardConfig) {
  const usedTimestamps = new Set<number>();
  const windowMs = (options?.window ?? 60) * 1000;
  const cleanupThreshold = options?.cleanupThreshold ?? 1000;

  function cleanup() {
    const cutoff = Date.now() - windowMs * 2;
    for (const ts of usedTimestamps) {
      if (ts < cutoff) {
        usedTimestamps.delete(ts);
      }
    }
  }

  return {
    /**
     * 验证时间戳有效性并标记为已使用
     */
    validate(timestamp: number): boolean {
      // 时间窗口检查
      if (!validateTimestamp(timestamp, options)) {
        return false;
      }

      // 重复检查
      if (usedTimestamps.has(timestamp)) {
        return false;
      }

      usedTimestamps.add(timestamp);

      // 定期清理
      if (usedTimestamps.size > cleanupThreshold) {
        cleanup();
      }

      return true;
    },

    /**
     * 清空已用时间戳记录
     */
    reset(): void {
      usedTimestamps.clear();
    },
  };
}
